// @flow

import * as React from 'react';

import type { PermissionsProps } from '@gandi/acl';
import { contactbook } from '@gandi/redux-resource-contactbook';
import type {
  FetchContactListAction,
  FavoriteContact,
} from '@gandi/redux-resource-contactbook';
import { sendPage } from '@gandi/tracking';
import { useTranslator, type TranslatorProps } from '@gandi/translate';
import { Base, Pills, PillsPanel, Spinner } from '@gandi/ui';

import { Layout } from '../../../components/Layout';
import type {
  WithRouterProps,
  PreFetchArgs,
  PreFetchReturn,
  FullState,
} from '../../../types';

import {
  getDisplayedRole,
  getDisplayedDomainProduct,
} from '../modules/helpers';

import { selectSetupOwner } from '../modules/selectors';

import type { CreateOrTransfer, EditableRole } from '../types';

import Create from './components/Create';
import Edit from './components/Edit';

import Select from './components/Select';

type Permissions = {
  canReadContactBook: boolean,
  canWriteContactBook: boolean,
};

export type Params = {
  process: CreateOrTransfer,
  role: EditableRole,
};

export type InjectedProps = {
  ...WithRouterProps<Params>,
  ...PermissionsProps,
  permissions: Permissions,
};

export type StateProps = {
  contactbookOrgId: string,
  isFetching: boolean,
  isContactListLoaded: boolean,

  shouldFetchContactList: boolean,
  contacts: Array<FavoriteContact>,
};

export type DispatchProps = {
  fetchContactList: FetchContactListAction<any>,
  getBaseContact: (
    translator: TranslatorProps,
  ) => Promise<(void => void) | void>,
};

export type Props = $Exact<{
  ...InjectedProps,
  ...DispatchProps,
  ...StateProps,
}>;

function Presenter({
  contactbookOrgId,
  contacts,
  fetchContactList,
  getBaseContact,
  isContactListLoaded,
  isFetching,
  isLoadedPermissions,
  params,
  permissions,
  shouldFetchContactList,
}: Props): React.Node {
  const translator = useTranslator();

  const [owner, setOwner] = React.useState();
  const [mode, setMode] = React.useState(0);

  const isSelectEnabled =
    permissions.canReadContactBook && contacts && !!contacts.length;

  React.useEffect(() => {
    (async function asyncLoader() {
      if (!owner) {
        const baseContact = await getBaseContact(translator);
        setOwner(baseContact);
      }
    })();
  }, [owner, setOwner, getBaseContact, translator]);

  React.useEffect(() => {
    if (shouldFetchContactList) {
      fetchContactList(contactbookOrgId, {});
    }
  }, [contactbookOrgId, shouldFetchContactList, fetchContactList]);

  React.useEffect(() => {
    const { process, role } = params;

    if (role) {
      const MODE_LABELS = isSelectEnabled
        ? ['modify', 'select', 'create']
        : ['modify', 'create'];
      return sendPage({
        chapter1: 'Shop',
        chapter2: 'Configure',
        chapter3: 'role_contact',
        name: `domain${process}-${role}-${MODE_LABELS[mode]}`,
      });
    }
    return undefined;
  }, [params, mode, isSelectEnabled]);

  const { __ } = translator;
  const { process, role } = params;

  const contactRole = getDisplayedRole(translator, role);
  const domainProcess = getDisplayedDomainProduct(translator, process);

  const title = __('%(contactRole)s / %(domainProcess)s', {
    contactRole,
    domainProcess,
  });

  const isLoaded =
    !isFetching &&
    isLoadedPermissions &&
    (isContactListLoaded || !shouldFetchContactList) &&
    !!owner;

  return (
    <Layout currentStep={1}>
      <Base tagName="h1" transform="capitalize">
        {title}
      </Base>
      {!isLoaded && <Spinner centered />}
      {isLoaded && (
        <Pills handleSelect={setMode}>
          <PillsPanel label={__('Edit Contact')}>
            <Edit
              process={process}
              role={role}
              canWriteContactBook={permissions.canWriteContactBook}
              translator={translator}
              owner={owner}
            />
          </PillsPanel>
          {isSelectEnabled && (
            <PillsPanel label={__('Select Contact')}>
              <Select process={process} role={role} contacts={contacts} />
            </PillsPanel>
          )}
          <PillsPanel label={__('Create Contact')}>
            <Create
              process={process}
              role={role}
              canWriteContactBook={permissions.canWriteContactBook}
              translator={translator}
            />
          </PillsPanel>
        </Pills>
      )}
    </Layout>
  );
}

Presenter.preFetch = async (
  args: PreFetchArgs<{}, void, empty, FullState>,
): PreFetchReturn<> => {
  const { dispatch, getState } = args;
  const contactbookOrgId = selectSetupOwner(getState());

  return Promise.allSettled([
    Select.preFetch(args),
    dispatch(contactbook.fetchContactList(contactbookOrgId, {})),
  ]);
};

export default Presenter;
